import React, { useState, useRef, useEffect, useCallback } from 'react';
import { HiOutlineX, HiOutlinePaperAirplane, HiOutlineSparkles, HiOutlineClipboardCopy, HiOutlineCheck, HiOutlinePencilAlt, HiOutlineBookmark, HiBookmark, HiOutlineTag } from 'react-icons/hi';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import SuggestedActionCard from './SuggestedActionCard';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useIsDesktop } from '../../hooks/useMediaQuery';

/**
 * Browser-local chat state must be keyed by tenant IDENTITY and user, never by
 * user alone and never by tenant NAME alone.
 *
 * User ids live in the tenant database, so id 3 in bestelectronicsltd and id 3 in
 * werisetechnologies are two different people. A user-only key made those two
 * collide, and signing out of one tenant and into the other on the same browser
 * replayed the previous tenant's transcript. That is worse than a display leak:
 * the panel posts the stored transcript back as `history` on the next question,
 * so one tenant's figures would be fed into the other tenant's prompt.
 *
 * The db name is not identity either: delete a company and register a new one
 * under the same name and both the db name AND the first user ids repeat, so a
 * name-keyed scope (v2) resurrected the deleted tenant's transcript inside the
 * new company. tenant_registry_id is the platform_tenants row id — the one value
 * that changes across such a recreation — so it, not the name, anchors the scope.
 * 'reg0' is the dev/single-tenant fallback where no registry row exists; there
 * is exactly one tenant in that world, so the db name alone is sufficient.
 */
const storageScope = (tenantDb, tenantRegistryId, userId) =>
  `${String(tenantDb || 'default').trim() || 'default'}.${tenantRegistryId ?? 'reg0'}.${userId ?? 'anon'}`;

const chatStorageKey = (scope) => `qai.chat.messages.${scope}.v3`;
const chatExpandedStorageKey = (scope) => `qai.chat.expanded.${scope}.v3`;
const contextPinStorageKey = (scope) => `qai.context.pin.${scope}.v3`;
const savedPromptsStorageKey = (scope) => `qai.saved.prompts.${scope}.v3`;
const CHAT_TRANSITION_MS = 280;

/**
 * Drop the superseded key generations. v1 keys were not tenant-scoped at all;
 * v2 keys were scoped by db NAME, which a recreated company reuses — so either
 * can be read by the wrong tenant, and one of them holds a full transcript.
 * There is no safe migration from v2: the client cannot tell whether a v2 entry
 * belongs to this tenant or to a deleted one that shared its name.
 */
const LEGACY_UNSCOPED_KEY_RE = /^qai\.(chat\.messages|chat\.expanded|context\.pin|saved\.prompts)\..*\.v[12]$/;

const purgeLegacyUnscopedChatKeys = () => {
  try {
    if (typeof window === 'undefined') return;
    const stale = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key && LEGACY_UNSCOPED_KEY_RE.test(key)) stale.push(key);
    }
    stale.forEach((key) => window.localStorage.removeItem(key));
  } catch (e) { /* ignore */ }
};

const loadStoredMessages = (scope) => {
  try {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem(chatStorageKey(scope)) : null;
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length) {
      return parsed.map((m) => ({ ...m, done: true }));
    }
    return [];
  } catch (e) {
    return [];
  }
};

const loadStoredExpandedState = (scope) => {
  try {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem(chatExpandedStorageKey(scope)) : null;
    return raw === 'true';
  } catch (e) {
    return false;
  }
};

const loadContextPin = (scope) => {
  try { return typeof window !== 'undefined' ? window.localStorage.getItem(contextPinStorageKey(scope)) || '' : ''; }
  catch { return ''; }
};

const loadSavedPrompts = (scope) => {
  try {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem(savedPromptsStorageKey(scope)) : null;
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
};

const defaultQuickQuestions = [
  "Summarize my business today",
  "What is my total revenue this month?",
  "Which customer owes the most money?",
  "Which items need production this week?",
  "Show customers on credit hold",
];

const pageQuickQuestions = {
  '/dashboard': [
    "Summarize my business today",
    "Summarise today's sales",
    "Top 5 customers this month",
    "Any overdue invoices?",
    "Cash position overview",
  ],
  '/customers': [
    "Which customer owes the most?",
    "Top 10 customers by revenue this year",
    "Customers with overdue balances",
    "Customers on credit hold",
    "New customers this month",
  ],
  '/credit-control': [
    "Customers on credit hold right now",
    "Open overdue reminders by priority",
    "Recent credit overrides this week",
    "Customers exceeding credit limit",
    "Onboarding drafts pending approval",
  ],
  '/suppliers': [
    "Which supplier do we owe the most?",
    "Total payables outstanding",
    "Top suppliers by purchase volume",
    "Supplier payment summary this month",
  ],
  '/inventory': [
    "Items below reorder level",
    "Top selling items this month",
    "Total inventory valuation",
    "Items with zero stock",
    "Open in-transit transfers",
    "Pending count variances",
  ],
  '/inventory/counts': [
    "Pending count variances",
    "Open count sessions",
    "Largest variances last 30 days",
    "Items most often recounted",
  ],
  '/inventory/intransit-transfers': [
    "Open in-transit transfers",
    "ITTs with unreceived quantity",
    "ITT variances this month",
  ],
  '/orders/invoices': [
    "How many open sales orders do I have?",
    "Overdue invoices right now",
    "Total sales this month",
    "Largest invoice this month",
    "Unpaid invoices summary",
  ],
  '/orders/purchase-orders': [
    "Total purchases this month",
    "GRVs pending posting",
    "Largest GRV this month",
    "Purchase summary by supplier",
  ],
  '/orders/credit-notes': [
    "Credit notes issued this month",
    "Total credit note value this year",
    "Customers with most credit notes",
    "Credit notes vs sales ratio",
  ],
  '/manufacturing': [
    "Which items need production this week?",
    "Open work orders by status",
    "WIP value right now",
    "Scrap by item last 30 days",
    "BOM costing for our top FG items",
  ],
  '/manufacturing/replenishment': [
    "Items where open SO exceeds WO + FG stock",
    "Top 10 items by net production needed",
    "Open work orders covering current demand",
  ],
  '/pos': [
    "POS sales today by terminal",
    "Cash variance per session this week",
    "Top selling POS items today",
    "Open POS sessions right now",
  ],
  '/delivery/manifests': [
    "Manifests dispatched this week",
    "Stops returned today",
    "Driver performance this month",
    "Open items in the return queue",
  ],
  '/crm': [
    "Open opportunities by stage",
    "Won deals this month",
    "Activities due this week",
    "Leads in review queue",
  ],
  '/finance/journals': [
    "I fueled the company car with KES 2,500 cash, how do I post it?",
    "I paid office rent KES 50,000 from the bank, what's the journal?",
    "We bought a laptop for KES 80,000 on credit, how do I book it?",
    "Paid M-Pesa transaction charges KES 120 — which accounts?",
    "Unposted draft journals",
    "Journal entries posted today",
  ],
  '/finance/journal-entries': [
    "I fueled the company car with KES 2,500 cash, how do I post it?",
    "I paid office rent KES 50,000 from the bank, what's the journal?",
    "We bought a laptop for KES 80,000 on credit, how do I book it?",
    "Unposted draft journals",
    "Journal entries posted today",
  ],
  '/finance/accounts': [
    "Bank and cash balances",
    "Revenue accounts summary",
    "Expense breakdown this month",
    "Which expense account should I use for fuel?",
    "Accounts with negative balances",
  ],
  '/finance/chart-of-accounts': [
    "Bank and cash balances",
    "Revenue accounts summary",
    "Expense breakdown this month",
    "Accounts with negative balances",
  ],
};

const MaximizeIcon = ({ className = 'w-5 h-5' }) => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" className={className} aria-hidden="true">
    <path d="M7 3H3v4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M13 3h4v4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3 13v4h4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M17 13v4h-4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3 7 7 3" strokeLinecap="round" />
    <path d="m13 3 4 4" strokeLinecap="round" />
    <path d="m3 13 4 4" strokeLinecap="round" />
    <path d="m13 17 4-4" strokeLinecap="round" />
  </svg>
);

const RestoreIcon = ({ className = 'w-5 h-5' }) => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" className={className} aria-hidden="true">
    <path d="M8 8H4V4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12 8h4V4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4 16v-4h4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M16 16v-4h-4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="m8 8-4-4" strokeLinecap="round" />
    <path d="m12 8 4-4" strokeLinecap="round" />
    <path d="m8 12-4 4" strokeLinecap="round" />
    <path d="m12 12 4 4" strokeLinecap="round" />
  </svg>
);

const getPageLabel = (currentPage) => {
  const path = String(currentPage || '/').split('?')[0];
  const segments = path.split('/').filter(Boolean);
  if (!segments.length) return 'Workspace Overview';
  return segments
    .slice(0, 2)
    .map((segment) => segment.replace(/-/g, ' '))
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' / ');
};

/* Typewriter component — streams text word-by-word like Claude / Gemini */
const TypewriterText = ({ text, speed = 30, onDone }) => {
  const [displayed, setDisplayed] = useState('');
  const idx = useRef(0);

  useEffect(() => {
    if (!text) return;
    idx.current = 0;
    setDisplayed('');
    const words = text.split(/(\s+)/);
    let wordIdx = 0;
    const tick = () => {
      if (wordIdx < words.length) {
        setDisplayed((d) => d + words[wordIdx]);
        wordIdx++;
        idx.current = requestAnimationFrame(() => setTimeout(tick, speed));
      } else {
        onDone?.();
      }
    };
    idx.current = requestAnimationFrame(() => setTimeout(tick, speed));
    return () => { cancelAnimationFrame(idx.current); clearTimeout(idx.current); };
  }, [text, speed, onDone]);

  return <>{displayed}<span className="inline-block w-[2px] h-[14px] bg-current opacity-40 ml-0.5 align-middle animate-pulse" style={{ display: displayed.length < text.length ? 'inline-block' : 'none' }} /></>;
};

const TABLE_SEPARATOR_RE = /^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?$/;
const HIGHLIGHT_LABELS = ['insight', 'key insight', 'recommendation', 'recommended action', 'action', 'warning', 'watchout', 'note', 'next step'];

const parseTableRow = (line) => line
  .trim()
  .replace(/^\|/, '')
  .replace(/\|$/, '')
  .split('|')
  .map((cell) => cell.trim());

const isMarkdownTableBlock = (lines) => (
  lines.length >= 2
  && lines[0].includes('|')
  && TABLE_SEPARATOR_RE.test(lines[1].trim())
);

const parseMarkdownTable = (lines) => {
  if (!isMarkdownTableBlock(lines)) return null;
  const headers = parseTableRow(lines[0]);
  const rows = lines.slice(2)
    .filter((line) => line.trim())
    .map((line) => parseTableRow(line))
    .filter((cells) => cells.length);

  if (!headers.length || !rows.length) return null;
  return { headers, rows };
};

const parseMetricLines = (lines) => {
  if (lines.length < 2 || lines.length > 6) return null;

  const metrics = lines.map((line) => {
    const match = line.match(/^([^:]{2,40}):\s+(.+)$/);
    if (!match) return null;
    const label = match[1].trim();
    const value = match[2].trim();
    // Advisory callout labels are never KPI tiles, even when the value contains numbers
    if (HIGHLIGHT_LABELS.includes(label.toLowerCase())) return null;
    const metricish = /(kes|usd|eur|%|x|days?|hours?|weeks?|months?|years?|\d)/i.test(value);
    if (!metricish) return null;
    return { label, value };
  });

  return metrics.every(Boolean) ? metrics : null;
};

const parseHighlightLine = (line) => {
  const match = line.match(/^([^:]{2,40}):\s+(.+)$/);
  if (!match) return null;
  const label = match[1].trim();
  const value = match[2].trim();
  if (!HIGHLIGHT_LABELS.includes(label.toLowerCase())) return null;
  return { label, value };
};

const cleanCellText = (text) =>
  text.replace(/\*\*/g, '').replace(/\[[^\]]+\]\([^)]+\)/g, (m) => m.match(/\[([^\]]+)\]/)?.[1] || '').trim();

const FormatText = ({ text, onNavigate, onAskAbout, isExpanded = false, isDark = false }) => {
  if (!text) return null;
  const lines = text.split('\n');

  const formatInline = (str) => {
    const tokens = str.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g);
    return tokens.map((tok, j) => {
      if (tok.startsWith('**') && tok.endsWith('**')) {
        return <strong key={j}>{tok.slice(2, -2)}</strong>;
      }
      const linkMatch = tok.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (linkMatch) {
        const [, label, href] = linkMatch;
        if (href.startsWith('/')) {
          return (
            <button
              key={j}
              onClick={() => onNavigate?.(href)}
              className="text-emerald-500 hover:text-emerald-400 underline underline-offset-2 font-medium"
            >
              {label}
            </button>
          );
        }
        return <span key={j}>{label}</span>;
      }
      return tok;
    });
  };

  if (!isExpanded) {
    return lines.map((line, i) => {
      const formatted = formatInline(line);
      const isBullet = /^[-•]\s/.test(line.trim());
      return (
        <span key={i}>
          {i > 0 && <br />}
          {isBullet ? <span className="ml-1">{formatted}</span> : formatted}
        </span>
      );
    });
  }

  const blocks = text.split(/\n\s*\n/).map((block) => block.trim()).filter(Boolean);

  return (
    <div className="space-y-4">
      {blocks.map((block, blockIndex) => {
        const blockLines = block.split('\n').map((line) => line.trimEnd()).filter((line) => line.trim().length > 0);
        const table = parseMarkdownTable(blockLines);
        const metrics = parseMetricLines(blockLines);
        const bulletLines = blockLines.every((line) => /^[-•]\s/.test(line.trim())) ? blockLines : null;
        const highlights = blockLines.map(parseHighlightLine);
        const allHighlights = highlights.every(Boolean) ? highlights : null;

        if (table) {
          return (
            <div key={blockIndex} className={`overflow-hidden rounded-2xl border ${isDark ? 'border-white/10 bg-white/[0.04]' : 'border-slate-200 bg-white/90'} shadow-[0_10px_28px_rgba(15,23,42,0.08)]`}>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200/70 dark:divide-white/10">
                  <thead className={isDark ? 'bg-white/[0.04]' : 'bg-slate-50/80'}>
                    <tr>
                      {table.headers.map((header, headerIndex) => (
                        <th key={headerIndex} className={`px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.14em] ${isDark ? 'text-slate-300' : 'text-slate-500'}`}>
                          {formatInline(header)}
                        </th>
                      ))}
                      {onAskAbout && <th className="w-10" />}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200/70 dark:divide-white/10">
                    {table.rows.map((row, rowIndex) => (
                      <tr key={rowIndex} className={`group ${isDark ? 'hover:bg-white/[0.03]' : 'hover:bg-slate-50/70'}`}>
                        {row.map((cell, cellIndex) => (
                          <td key={cellIndex} className={`px-4 py-3 align-top text-sm leading-6 ${isDark ? 'text-slate-100' : 'text-slate-700'}`}>
                            {formatInline(cell)}
                          </td>
                        ))}
                        {onAskAbout && (
                          <td className="px-2 py-3 align-top">
                            <button
                              onClick={() => onAskAbout(`Drill into ${cleanCellText(row[0])}`)}
                              title={`Ask about ${cleanCellText(row[0])}`}
                              className={`opacity-0 group-hover:opacity-100 transition-opacity text-[11px] px-2 py-0.5 rounded-full border ${isDark ? 'border-white/10 text-emerald-400 hover:bg-white/10' : 'border-emerald-200 text-emerald-600 hover:bg-emerald-50'}`}
                            >
                              Ask →
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        }

        // Callout cards take precedence over KPI tiles — a "Warning: ..." block
        // with numbers must never render as metric tiles
        if (allHighlights) {
          const calloutStyle = (label) => {
            const key = label.toLowerCase();
            if (key.includes('warning') || key.includes('watchout')) {
              return {
                card: isDark ? 'border-l-amber-300 border-white/10 bg-amber-400/[0.08]' : 'border-l-amber-500 border-slate-200 bg-amber-50/90',
                label: isDark ? 'text-amber-300' : 'text-amber-700',
              };
            }
            if (key.includes('recommend') || key.includes('action') || key.includes('next step')) {
              return {
                card: isDark ? 'border-l-sky-300 border-white/10 bg-sky-400/[0.08]' : 'border-l-sky-500 border-slate-200 bg-sky-50/90',
                label: isDark ? 'text-sky-300' : 'text-sky-700',
              };
            }
            return {
              card: isDark ? 'border-l-emerald-300 border-white/10 bg-emerald-400/[0.08]' : 'border-l-emerald-500 border-slate-200 bg-emerald-50/90',
              label: isDark ? 'text-emerald-300' : 'text-emerald-700',
            };
          };
          return (
            <div key={blockIndex} className="space-y-3">
              {allHighlights.map((item, itemIndex) => {
                const style = calloutStyle(item.label);
                return (
                  <div key={itemIndex} className={`rounded-2xl border-l-4 px-4 py-3 ${style.card}`}>
                    <div className={`text-[11px] font-semibold uppercase tracking-[0.14em] ${style.label}`}>
                      {item.label}
                    </div>
                    <div className={`mt-1 text-sm leading-6 ${isDark ? 'text-slate-100' : 'text-slate-700'}`}>
                      {formatInline(item.value)}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        }

        if (metrics) {
          return (
            <div key={blockIndex} className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {metrics.map((metric, metricIndex) => (
                <div key={metricIndex} className={`rounded-2xl border p-4 ${isDark ? 'border-white/10 bg-white/[0.05]' : 'border-slate-200 bg-white/92'} shadow-[0_12px_28px_rgba(15,23,42,0.08)]`}>
                  <div className={`text-[11px] font-semibold uppercase tracking-[0.14em] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    {metric.label}
                  </div>
                  <div className={`mt-2 text-lg font-semibold leading-7 ${isDark ? 'text-slate-50' : 'text-slate-900'}`}>
                    {formatInline(metric.value)}
                  </div>
                </div>
              ))}
            </div>
          );
        }

        if (bulletLines) {
          return (
            <ul key={blockIndex} className="space-y-2.5 pl-1">
              {bulletLines.map((line, lineIndex) => (
                <li key={lineIndex} className="flex items-start gap-3">
                  <span className={`mt-2 h-2 w-2 rounded-full ${isDark ? 'bg-emerald-300' : 'bg-emerald-500'}`} />
                  <span className={`min-w-0 text-sm leading-6 ${isDark ? 'text-slate-100' : 'text-slate-700'}`}>
                    {formatInline(line.replace(/^[-•]\s*/, ''))}
                  </span>
                </li>
              ))}
            </ul>
          );
        }

        return (
          <div key={blockIndex} className={`space-y-3 text-sm leading-7 ${isDark ? 'text-slate-100' : 'text-slate-700'}`}>
            {blockLines.map((line, lineIndex) => {
              const headingMatch = line.match(/^#{1,3}\s+(.+)$/);
              if (headingMatch) {
                return (
                  <h4 key={lineIndex} className={`text-base font-semibold ${isDark ? 'text-slate-50' : 'text-slate-900'}`}>
                    {formatInline(headingMatch[1])}
                  </h4>
                );
              }
              return <p key={lineIndex}>{formatInline(line)}</p>;
            })}
          </div>
        );
      })}
    </div>
  );
};

const CopyButton = ({ text, isDark }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <button
      onClick={handleCopy}
      title={copied ? 'Copied!' : 'Copy response'}
      className={`p-1 rounded transition-colors ${
        isDark
          ? 'text-slate-500 hover:text-slate-300 hover:bg-white/10'
          : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
      }`}
    >
      {copied
        ? <HiOutlineCheck className="w-3.5 h-3.5 text-emerald-400" />
        : <HiOutlineClipboardCopy className="w-3.5 h-3.5" />
      }
    </button>
  );
};

const FeedbackButton = ({ msgIndex, feedback, onFeedback, isDark }) => (
  <>
    <button
      onClick={() => onFeedback(msgIndex, 'positive')}
      title="Helpful"
      className={`p-1 rounded transition-colors ${
        feedback === 'positive'
          ? 'text-emerald-400'
          : isDark ? 'text-slate-500 hover:text-slate-300 hover:bg-white/10' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
      }`}
    >
      <HiOutlineCheck className="w-3.5 h-3.5" />
    </button>
    <button
      onClick={() => onFeedback(msgIndex, 'negative')}
      title="Not helpful"
      className={`p-1 rounded transition-colors ${
        feedback === 'negative'
          ? 'text-rose-400'
          : isDark ? 'text-slate-500 hover:text-slate-300 hover:bg-white/10' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
      }`}
    >
      <HiOutlineX className="w-3.5 h-3.5" />
    </button>
  </>
);

const StarButton = ({ prompt, isSaved, onSave, onRemove, isDark }) => (
  <button
    onClick={() => isSaved ? onRemove(prompt) : onSave(prompt)}
    title={isSaved ? 'Remove from saved prompts' : 'Save prompt'}
    className={`p-1 rounded transition-all ${
      isSaved
        ? 'text-amber-400 opacity-100'
        : isDark
          ? 'text-slate-400 opacity-40 hover:opacity-100 hover:text-amber-300 hover:bg-white/10'
          : 'text-gray-400 opacity-40 hover:opacity-100 hover:text-amber-500 hover:bg-gray-100'
    }`}
  >
    {isSaved ? <HiBookmark className="w-3.5 h-3.5" /> : <HiOutlineBookmark className="w-3.5 h-3.5" />}
  </button>
);

const CHART_DATA_BLOCK_RE = /```chart-data\s*([\s\S]*?)```/gi;
const MAX_CHARTS_PER_MESSAGE = 3;

const normalizeChartData = (raw) => {
  if (!raw || !raw.type) return null;
  // Legacy/loose shape: {xKey, yKey, data: [...]} → normalize to series [{label, value}]
  if (['bar', 'line', 'pie'].includes(raw.type) && !Array.isArray(raw.series)
      && Array.isArray(raw.data) && raw.xKey && raw.yKey) {
    const series = raw.data
      .map((row) => ({ label: String(row[raw.xKey] ?? ''), value: Number(row[raw.yKey]) || 0 }))
      .filter((point) => point.label);
    return series.length > 0 ? { ...raw, series } : null;
  }
  if (['bar', 'line', 'pie'].includes(raw.type) && Array.isArray(raw.series) && raw.series.length > 0) {
    return raw;
  }
  if (raw.type === 'table' && Array.isArray(raw.columns) && Array.isArray(raw.rows) && raw.rows.length > 0) {
    return raw;
  }
  return null;
};

const parseChartDataBlocks = (content) => {
  if (!content || typeof content !== 'string') return { content, charts: [] };
  const charts = [];
  const stripped = content.replace(CHART_DATA_BLOCK_RE, (fullMatch, json) => {
    if (charts.length < MAX_CHARTS_PER_MESSAGE) {
      try {
        const chart = normalizeChartData(JSON.parse(json.trim()));
        if (chart) charts.push(chart);
      } catch { /* malformed block — drop it, keep the text */ }
    }
    return '';
  }).trim();
  // Blocks are always stripped — malformed chart JSON should never render as text
  return { content: stripped, charts };
};

const CHART_COLORS = ['#10b981', '#0d9488', '#06b6d4', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#ec4899', '#84cc16', '#f97316'];
const VIZ_KEYWORDS_RE = /\b(bar chart|pie chart|line chart|line graph|chart|graph|tabulate|visuali[sz]e|histogram)\b/i;
const OVERVIEW_KEYWORDS_RE = /\b(business\s+(overview|health|summary|snapshot|report|status)|overview\s+of\s+(my\s+)?business|how\s+is\s+(my\s+)?business(\s+doing)?|company\s+(overview|health|summary|snapshot)|executive\s+summary|give\s+me\s+(a\s+)?(full\s+)?overview|business\s+performance)\b/i;

const fmtChartNum = (v, monetary) => {
  if (typeof v !== 'number') return String(v);
  if (!monetary) return v.toLocaleString();
  if (v >= 1_000_000) return `KES ${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `KES ${(v / 1_000).toFixed(0)}K`;
  return `KES ${v.toLocaleString()}`;
};

const ChartBlock = ({ chart, isDark }) => {
  if (!chart) return null;

  const monetary = /revenue|sales|amount|value|cost|payment|balance|total|profit|loss|price/i.test(
    (chart.y_label || '') + (chart.title || '')
  );
  const fmt = (v) => fmtChartNum(v, monetary);

  const gridColor = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)';
  const tickStyle = { fontSize: 11, fill: isDark ? '#94a3b8' : '#64748b' };
  const tooltipStyle = {
    backgroundColor: isDark ? '#0f172a' : '#fff',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
    borderRadius: 12,
    color: isDark ? '#f1f5f9' : '#1e293b',
    fontSize: 12,
  };

  const handleExport = () => {
    try {
      let ws;
      if (chart.type === 'table') {
        ws = XLSX.utils.aoa_to_sheet([chart.columns, ...chart.rows]);
      } else {
        ws = XLSX.utils.json_to_sheet(
          (chart.series || []).map((s) => ({ [chart.x_label || 'Label']: s.label, [chart.y_label || 'Value']: s.value }))
        );
      }
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Data');
      XLSX.writeFile(wb, `${(chart.title || 'chart').replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, '_').substring(0, 40)}.xlsx`);
    } catch { /* ignore export errors */ }
  };

  return (
    <div className={`rounded-2xl border overflow-hidden ${isDark ? 'border-white/10 bg-white/[0.025]' : 'border-slate-200 bg-white/95'} shadow-[0_12px_32px_rgba(15,23,42,0.10)]`}>
      {/* Title bar */}
      <div className={`flex items-start justify-between gap-3 px-5 py-3.5 border-b ${isDark ? 'border-white/10 bg-white/[0.03]' : 'border-slate-100 bg-slate-50/70'}`}>
        <div className="min-w-0">
          <p className={`text-sm font-semibold leading-5 ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>{chart.title}</p>
          {chart.type !== 'table' && (chart.x_label || chart.y_label) && (
            <p className={`mt-0.5 text-[11px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
              {[chart.x_label, chart.y_label].filter(Boolean).join(' · ')}
            </p>
          )}
          {chart.type === 'table' && (
            <p className={`mt-0.5 text-[11px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
              {chart.rows?.length} rows · {chart.columns?.length} columns
            </p>
          )}
        </div>
        <button
          onClick={handleExport}
          className={`flex-shrink-0 text-[11px] px-2.5 py-1 rounded-lg border font-medium transition-colors ${
            isDark ? 'border-white/10 text-slate-300 hover:bg-white/10' : 'border-slate-200 text-slate-600 hover:bg-slate-100'
          }`}
        >
          Export .xlsx
        </button>
      </div>

      {/* Chart */}
      <div className={chart.type === 'table' ? '' : 'px-3 pt-5 pb-3'}>
        {chart.type === 'bar' && (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={chart.series} margin={{ top: 4, right: 16, left: 4, bottom: chart.series?.length > 5 ? 72 : 24 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
              <XAxis
                dataKey="label"
                angle={chart.series?.length > 6 ? -38 : 0}
                textAnchor={chart.series?.length > 6 ? 'end' : 'middle'}
                tick={tickStyle}
                interval={0}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tickFormatter={fmt}
                tick={tickStyle}
                axisLine={false}
                tickLine={false}
                width={monetary ? 76 : 44}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(v) => [fmt(v), chart.y_label || 'Value']}
                cursor={{ fill: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)' }}
              />
              <Bar dataKey="value" radius={[5, 5, 0, 0]} maxBarSize={52}>
                {chart.series?.map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} fillOpacity={0.88} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}

        {chart.type === 'line' && (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chart.series} margin={{ top: 4, right: 16, left: 4, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis dataKey="label" tick={tickStyle} axisLine={false} tickLine={false} interval="preserveStartEnd" />
              <YAxis tickFormatter={fmt} tick={tickStyle} axisLine={false} tickLine={false} width={monetary ? 76 : 44} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => [fmt(v), chart.y_label || 'Value']} />
              <Line
                type="monotone"
                dataKey="value"
                stroke="#10b981"
                strokeWidth={2.5}
                dot={{ fill: '#10b981', r: 3.5, strokeWidth: 0 }}
                activeDot={{ r: 6, fill: '#10b981' }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}

        {chart.type === 'pie' && (
          <ResponsiveContainer width="100%" height={320}>
            <PieChart>
              <Pie
                data={chart.series}
                dataKey="value"
                nameKey="label"
                cx="50%"
                cy="48%"
                outerRadius={118}
                innerRadius={40}
                paddingAngle={2}
              >
                {chart.series?.map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} stroke="none" />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => [fmt(v), 'Value']} />
              <Legend
                iconType="circle"
                iconSize={8}
                wrapperStyle={{ fontSize: 11, color: isDark ? '#94a3b8' : '#64748b', paddingTop: 8 }}
                formatter={(value) => value.length > 22 ? value.substring(0, 22) + '…' : value}
              />
            </PieChart>
          </ResponsiveContainer>
        )}

        {chart.type === 'table' && (
          <div className="overflow-auto max-h-[28rem]">
            <table className="min-w-full text-sm">
              <thead className={`sticky top-0 ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}>
                <tr>
                  {chart.columns?.map((col, i) => (
                    <th key={i} className={`px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.12em] whitespace-nowrap border-b ${isDark ? 'text-slate-400 border-white/10' : 'text-slate-500 border-slate-200'}`}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'divide-white/[0.06]' : 'divide-slate-100'}`}>
                {chart.rows?.map((row, ri) => (
                  <tr key={ri} className={`transition-colors ${isDark ? 'hover:bg-white/[0.03]' : 'hover:bg-slate-50/70'}`}>
                    {row.map((cell, ci) => (
                      <td
                        key={ci}
                        className={`px-4 py-2.5 text-[13px] leading-5 whitespace-nowrap ${
                          typeof cell === 'number' ? 'text-right font-medium tabular-nums' : ''
                        } ${isDark ? 'text-slate-200' : 'text-slate-700'}`}
                      >
                        {typeof cell === 'number' && monetary ? fmt(cell) : cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const AIChatInner = ({ onClose, currentPage = '/', isSidebarCollapsed = false, scope }) => {
  const { isDark } = useTheme();
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const pageLabel = getPageLabel(currentPage);

  const quickQuestions = (() => {
    const path = currentPage.toLowerCase();
    for (const [prefix, questions] of Object.entries(pageQuickQuestions)) {
      if (path.startsWith(prefix)) return questions;
    }
    return defaultQuickQuestions;
  })();

  const handleDrillDown = (href) => {
    navigate(href);
    onClose();
  };

  const [messages, setMessages] = useState(() => loadStoredMessages(scope));
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [isExpanded, setIsExpanded] = useState(() => loadStoredExpandedState(scope));
  const [isMorphing, setIsMorphing] = useState(false);
  const [launchGlow, setLaunchGlow] = useState(true);

  // Context pin — persistent focus/scope injected into every message
  const [contextPin, setContextPin] = useState(() => loadContextPin(scope));
  const [showContextInput, setShowContextInput] = useState(false);
  const [contextInputDraft, setContextInputDraft] = useState('');

  // Saved prompts — user-bookmarked questions
  const [savedPrompts, setSavedPrompts] = useState(() => loadSavedPrompts(scope));

  const messagesEndRef = useRef(null);
  const chatRef = useRef(null);
  const contextInputRef = useRef(null);

  // Measure the shell so the animated border maps 1:1 to real pixels.
  // (A fixed/stretched viewBox distorts the dash speed and makes it jitter.)
  const shellRef = useRef(null);
  const [shellSize, setShellSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = shellRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const box = entries[0]?.borderBoxSize?.[0];
      const w = box ? box.inlineSize : entries[0]?.contentRect?.width;
      const h = box ? box.blockSize : entries[0]?.contentRect?.height;
      if (w && h) setShellSize({ w, h });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const hasUserMessages = messages.some((m) => m.role === 'user');
  const isAiActive = loading || streaming || launchGlow;

  // ── Styles ────────────────────────────────────────────────────────────────
  const panelShellClass = isDark
    ? 'bg-slate-950/72 backdrop-blur-2xl shadow-[0_30px_90px_rgba(2,6,23,0.55)] border border-white/10'
    : 'bg-white/72 backdrop-blur-2xl shadow-[0_30px_90px_rgba(15,23,42,0.18)] border border-white/60';
  const panelInnerClass = isDark
    ? 'border-l border-white/10 bg-white/[0.03]'
    : 'border-l border-white/40 bg-white/40';
  const headerClass = isDark
    ? 'bg-white/5 text-white border-b border-white/10'
    : 'bg-white/65 text-navy-900 border-b border-white/50';
  const assistantBubbleClass = isDark
    ? 'bg-white/10 text-slate-50 border border-white/10 shadow-[0_12px_28px_rgba(15,23,42,0.22)] backdrop-blur-sm'
    : 'bg-white/80 text-gray-800 border border-white/70 shadow-[0_12px_28px_rgba(15,23,42,0.10)] backdrop-blur-sm';
  const userBubbleClass = isDark
    ? 'bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-600 text-white border border-emerald-300/20 shadow-[0_14px_32px_rgba(16,185,129,0.28)]'
    : 'bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-600 text-white border border-emerald-400/15 shadow-[0_14px_32px_rgba(16,185,129,0.22)]';
  const quickQuestionClass = isDark
    ? 'text-xs bg-white/8 text-slate-200 px-3 py-1.5 rounded-full border border-white/10 hover:bg-white/14 hover:text-white hover:border-white/20 transition-colors backdrop-blur-sm'
    : 'text-xs bg-white/85 text-gray-700 border border-white/70 px-3 py-1.5 rounded-full hover:bg-emerald-50 hover:border-emerald-200 hover:text-emerald-700 transition-colors backdrop-blur-sm';
  const inputClass = isDark
    ? 'flex-1 px-3 py-2 text-sm border border-white/10 bg-white/8 text-slate-50 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-400 transition-colors placeholder:text-slate-400 backdrop-blur-sm'
    : 'flex-1 px-3 py-2 text-sm border border-white/60 bg-white/85 text-gray-900 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors placeholder:text-gray-400 backdrop-blur-sm';
  const shellRadiusClass = isExpanded ? 'rounded-[28px]' : 'rounded-bl-[24px] rounded-br-[24px]';
  const innerRadiusClass = isExpanded ? 'rounded-[26px]' : 'rounded-bl-[22px] rounded-br-[22px]';

  // Pixel-accurate rounded-rect path for the animated border. Square top / rounded
  // bottom when collapsed; fully rounded when expanded. Drawn in real px (no viewBox
  // stretch) so the glow travels at a constant speed the whole way around.
  const borderPath = (() => {
    const { w, h } = shellSize;
    if (!w || !h) return '';
    const i = 1.4;                            // stroke inset from the edge
    const rt = isExpanded ? 26 : 0;           // top corner radius
    const rb = isExpanded ? 26 : 22;          // bottom corner radius
    const x0 = i, y0 = i, x1 = w - i, y1 = h - i;
    return [
      `M ${x0 + rt} ${y0}`,
      `H ${x1 - rt}`,
      rt ? `A ${rt} ${rt} 0 0 1 ${x1} ${y0 + rt}` : `L ${x1} ${y0}`,
      `V ${y1 - rb}`,
      `A ${rb} ${rb} 0 0 1 ${x1 - rb} ${y1}`,
      `H ${x0 + rb}`,
      `A ${rb} ${rb} 0 0 1 ${x0} ${y1 - rb}`,
      `V ${y0 + rt}`,
      rt ? `A ${rt} ${rt} 0 0 1 ${x0 + rt} ${y0}` : `L ${x0} ${y0}`,
      'Z',
    ].join(' ');
  })();
  // On mobile the sidebar is an off-canvas drawer, so the expanded panel spans to the left edge.
  const sidebarOffset = !isDesktop ? 0 : (isSidebarCollapsed ? 64 : 256);
  const collapsedLeft = 'max(calc(100vw - 29rem), 1rem)';
  const containerClassName = `fixed z-50 animate-[aiPanelIn_180ms_ease-out] transition-[top,right,bottom,left] duration-[${CHAT_TRANSITION_MS}ms] ease-[cubic-bezier(0.22,1,0.36,1)]`;
  const containerStyle = isExpanded
    ? { top: 0, right: 0, bottom: 0, left: `${sidebarOffset}px` }
    : { top: 0, right: 0, bottom: 0, left: collapsedLeft };
  const panelMotionClass = isMorphing
    ? 'scale-[0.992] opacity-[0.985]'
    : 'scale-100 opacity-100';
  const messageViewportClass = isExpanded ? 'flex-1 overflow-y-auto px-6 py-6 relative z-10' : 'flex-1 overflow-y-auto p-4 space-y-4 relative z-10';
  const conversationRailClass = isExpanded ? 'mx-auto w-full max-w-5xl space-y-6' : 'space-y-4';
  const assistantMessageWidthClass = isExpanded ? 'w-full max-w-[min(100%,62rem)] group' : 'max-w-[92%] group';
  const userMessageWidthClass = isExpanded ? 'w-full max-w-2xl group' : 'max-w-[92%] group';
  const assistantMessageCardClass = isExpanded
    ? `${assistantBubbleClass} rounded-[26px] px-5 py-4 text-[15px] leading-7`
    : `${assistantBubbleClass} rounded-bl-sm px-3.5 py-2.5 text-sm leading-relaxed`;
  const userMessageCardClass = isExpanded
    ? `${userBubbleClass} rounded-[24px] px-4 py-3 text-sm leading-6 font-medium`
    : `${userBubbleClass} rounded-br-sm px-3.5 py-2.5 text-sm leading-relaxed font-medium`;

  // ── Effects ───────────────────────────────────────────────────────────────
  useEffect(() => { purgeLegacyUnscopedChatKeys(); }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streaming]);

  useEffect(() => {
    try {
      const toStore = messages.filter((m) => m.done);
      window.localStorage.setItem(chatStorageKey(scope), JSON.stringify(toStore));
    } catch (e) { /* ignore */ }
  }, [messages, scope]);

  useEffect(() => {
    const timer = setTimeout(() => setLaunchGlow(false), 2200);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(chatExpandedStorageKey(scope), isExpanded ? 'true' : 'false');
    } catch (e) { /* ignore */ }
  }, [isExpanded, scope]);

  useEffect(() => {
    if (!isMorphing) return undefined;
    const timer = setTimeout(() => setIsMorphing(false), CHAT_TRANSITION_MS);
    return () => clearTimeout(timer);
  }, [isMorphing]);

  useEffect(() => {
    try { window.localStorage.setItem(contextPinStorageKey(scope), contextPin); } catch { /* ignore */ }
  }, [contextPin, scope]);

  useEffect(() => {
    try { window.localStorage.setItem(savedPromptsStorageKey(scope), JSON.stringify(savedPrompts)); } catch { /* ignore */ }
  }, [savedPrompts, scope]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      if (showContextInput) { setShowContextInput(false); return; }
      if (isExpanded) { event.preventDefault(); setIsExpanded(false); }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isExpanded, showContextInput]);

  // Focus context input when it opens
  useEffect(() => {
    if (showContextInput) {
      setContextInputDraft(contextPin);
      setTimeout(() => contextInputRef.current?.focus(), 50);
    }
  }, [showContextInput, contextPin]);

  const markLastDone = useCallback(() => {
    setMessages((prev) => {
      const copy = [...prev];
      if (copy.length) copy[copy.length - 1] = { ...copy[copy.length - 1], done: true };
      return copy;
    });
    setStreaming(false);
  }, []);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleNewChat = useCallback(() => {
    setMessages([]);
    setInput('');
    setStreaming(false);
    setLoading(false);
    try { window.localStorage.removeItem(chatStorageKey(scope)); } catch (e) { /* ignore */ }
  }, [scope]);

  const handleSetContextPin = useCallback(() => {
    setContextPin(contextInputDraft.trim());
    setShowContextInput(false);
  }, [contextInputDraft]);

  const handleClearContextPin = useCallback(() => {
    setContextPin('');
    setContextInputDraft('');
    setShowContextInput(false);
  }, []);

  const handleSavePrompt = useCallback((text) => {
    setSavedPrompts((prev) => {
      if (prev.includes(text)) return prev;
      return [text, ...prev].slice(0, 12);
    });
  }, []);

  const handleRemoveSavedPrompt = useCallback((text) => {
    setSavedPrompts((prev) => prev.filter((p) => p !== text));
  }, []);

  const handleFeedback = useCallback(async (msgIndex, rating) => {
    setMessages((prev) => prev.map((m, i) => i === msgIndex ? { ...m, feedback: rating } : m));
    const userMsg = messages[msgIndex - 1];
    const assistantMsg = messages[msgIndex];
    try {
      await api.post('/ai/feedback', {
        prompt: userMsg?.content || '',
        response: assistantMsg?.content || '',
        rating,
        page: currentPage,
      });
    } catch { /* non-critical */ }
  }, [messages, currentPage]);

  const sendMessage = async (text) => {
    const userMessage = text || input.trim();
    if (!userMessage || loading) return;

    // Auto-expand to full screen for visualization or business overview requests
    const isOverview = OVERVIEW_KEYWORDS_RE.test(userMessage);
    if ((VIZ_KEYWORDS_RE.test(userMessage) || isOverview) && !isExpanded) {
      setIsExpanded(true);
      setIsMorphing(true);
    }

    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: userMessage, done: true }]);
    setLoading(true);

    try {
      const res = await api.post('/ai/chat', {
        message: userMessage,
        history: messages.filter(m => m.done).slice(-10),
        contextPin: contextPin || undefined,
        intent: isOverview ? 'business_overview' : undefined,
        page: currentPage,
      });
      setLoading(false);
      setStreaming(true);

      // Action blocks are extracted server-side now and arrive as descriptors —
      // already resolved to real customer/supplier/item rows and already checked
      // against this user's permissions. Only chart blocks are still parsed here.
      const parsedChart = parseChartDataBlocks(res.data.reply || '');
      const actions = Array.isArray(res.data.actions) ? res.data.actions : [];

      if ((parsedChart.charts.length > 0 || actions.length > 1) && !isExpanded) {
        setIsExpanded(true);
        setIsMorphing(true);
      }

      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: parsedChart.content,
          actions,
          charts: parsedChart.charts,
          follow_ups: Array.isArray(res.data.follow_ups) ? res.data.follow_ups : [],
          done: false,
        },
      ]);
    } catch (err) {
      setLoading(false);
      const errMsg = err.response?.data?.message || 'Sorry, I encountered an error processing your request. Please try again.';
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: errMsg, done: true },
      ]);
    }
  };

  // A card that executed stays on screen showing what it created. Persisting the
  // result here (rather than only in the card's own state) is what makes the
  // "Created: RCT-0041" survive a re-render or a panel collapse.
  const handleActionCompleted = useCallback((actionId, result) => {
    setMessages((prev) => prev.map((m) => (
      m.actions?.some((a) => a.action_id === actionId)
        ? { ...m, actions: m.actions.map((a) => (a.action_id === actionId ? { ...a, completed: result } : a)) }
        : m
    )));
  }, []);

  const handleActionNavigate = useCallback((route) => {
    navigate(route, { state: { fromAI: true } });
    onClose?.();
  }, [navigate, onClose]);

  const toggleExpanded = useCallback(() => {
    setIsMorphing(true);
    setIsExpanded((prev) => !prev);
  }, []);

  return (
    <div className={containerClassName} style={containerStyle}>
      <div ref={shellRef} className={`relative h-full w-full overflow-hidden ${shellRadiusClass} ${panelShellClass} ${panelMotionClass} transition-[border-radius,box-shadow,transform,opacity] duration-[${CHAT_TRANSITION_MS}ms] ease-[cubic-bezier(0.22,1,0.36,1)] will-change-[top,left,right,bottom,transform]`}>
        <div className={`absolute top-0 left-0 right-0 h-[3px] ${isDark ? 'bg-gradient-to-r from-cyan-300 via-emerald-400 to-amber-300' : 'bg-gradient-to-r from-sky-400 via-emerald-500 to-amber-400'}`} />
        <div className={`absolute -top-16 -right-20 rounded-full blur-3xl pointer-events-none ${isExpanded ? 'h-96 w-96' : 'h-48 w-48'} ${isDark ? 'bg-emerald-500/10' : 'bg-emerald-200/40'}`} />
        <div className={`absolute top-32 -left-20 rounded-full blur-3xl pointer-events-none ${isExpanded ? 'h-[28rem] w-[28rem]' : 'h-52 w-52'} ${isDark ? 'bg-cyan-500/10' : 'bg-sky-200/35'}`} />
        <div className={`absolute rounded-full blur-3xl pointer-events-none ${isExpanded ? 'bottom-40 right-1/4 h-80 w-80' : 'bottom-28 right-10 h-40 w-40'} ${isDark ? 'bg-amber-400/10' : 'bg-amber-200/30'}`} />
        <svg
          className="absolute inset-0 h-full w-full pointer-events-none"
          style={{ overflow: 'visible' }}
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="q-ai-chat-border-base" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={isDark ? 'rgba(125,211,252,0.35)' : 'rgba(56,189,248,0.28)'} />
              <stop offset="30%" stopColor={isDark ? 'rgba(34,211,238,0.35)' : 'rgba(45,212,191,0.26)'} />
              <stop offset="60%" stopColor={isDark ? 'rgba(250,204,21,0.28)' : 'rgba(250,204,21,0.22)'} />
              <stop offset="100%" stopColor={isDark ? 'rgba(52,211,153,0.32)' : 'rgba(16,185,129,0.24)'} />
            </linearGradient>
            <linearGradient id="q-ai-chat-border-active" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={isDark ? '#60a5fa' : '#0284c7'} />
              <stop offset="25%" stopColor={isDark ? '#22d3ee' : '#0d9488'} />
              <stop offset="50%" stopColor={isDark ? '#34d399' : '#10b981'} />
              <stop offset="75%" stopColor={isDark ? '#f59e0b' : '#d97706'} />
              <stop offset="100%" stopColor={isDark ? '#f472b6' : '#ec4899'} />
            </linearGradient>
            <filter id="q-ai-chat-border-glow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="2.2" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          {borderPath && (
            <>
              <path
                d={borderPath}
                fill="none"
                stroke="url(#q-ai-chat-border-base)"
                strokeWidth="1.8"
              />
              <path
                d={borderPath}
                fill="none"
                stroke="url(#q-ai-chat-border-active)"
                strokeWidth="2.1"
                strokeLinecap="round"
                pathLength={190}
                strokeDasharray="80 30 50 30"
                filter="url(#q-ai-chat-border-glow)"
                style={{
                  opacity: isAiActive ? 1 : 0.48,
                  animation: isAiActive ? 'aiPanelDashMove 3.8s linear infinite, aiPanelGlowBreath 2.8s ease-in-out infinite' : 'none',
                }}
              />
            </>
          )}
        </svg>

        <div className={`relative h-full w-full p-[2px] ${shellRadiusClass}`}>
          <div className={`h-full w-full flex flex-col ${innerRadiusClass} ${panelInnerClass}`}>

          {/* ── Header ─────────────────────────────────────────────────────── */}
          <div className={`flex items-center justify-between px-4 py-3 ${headerClass} backdrop-blur-xl ${isExpanded ? 'sticky top-0 z-20 px-6 py-4' : ''}`}>
            <div className="flex items-center gap-3 min-w-0">
              <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${isDark ? 'bg-white/10 text-emerald-300 border border-white/10' : 'bg-white/85 text-emerald-600 border border-white/60'} ${isExpanded ? 'w-10 h-10 rounded-2xl' : ''}`}>
                <HiOutlineSparkles className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-[15px] tracking-[0.01em] leading-4">Q AI</p>
                <p className={`text-[11px] mt-0.5 truncate ${isDark ? 'text-slate-300' : 'text-gray-500'}`}>Business Copilot</p>
                {contextPin && (
                  <div className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] border ${isDark ? 'bg-emerald-400/10 text-emerald-300 border-emerald-400/20' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
                    <HiOutlineTag className="w-2.5 h-2.5 flex-shrink-0" />
                    <span className="truncate max-w-[110px]">{contextPin}</span>
                    <button
                      onClick={handleClearContextPin}
                      className={`ml-0.5 leading-none hover:text-rose-400 transition-colors`}
                      title="Clear context"
                    >×</button>
                  </div>
                )}
                {isExpanded && (
                  <div className="mt-1 flex items-center gap-1.5">
                    <span className={`text-[10px] font-medium ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                      {pageLabel}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={handleNewChat}
                title="Start a new chat"
                disabled={loading || streaming}
                className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  isDark
                    ? 'text-slate-300 hover:bg-white/10 hover:text-emerald-300'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-emerald-600'
                }`}
              >
                <HiOutlinePencilAlt className="w-4 h-4" />
                <span className="hidden sm:inline">New chat</span>
              </button>
              <button
                onClick={() => setShowContextInput((v) => !v)}
                title={contextPin ? `Context: ${contextPin}` : 'Set context filter'}
                className={`p-1 rounded transition-colors ${
                  contextPin
                    ? (isDark ? 'text-emerald-400 hover:bg-white/10' : 'text-emerald-600 hover:bg-gray-100')
                    : (isDark ? 'text-slate-400 hover:bg-white/10 hover:text-slate-200' : 'text-gray-500 hover:bg-gray-100')
                }`}
              >
                <HiOutlineTag className="w-4 h-4" />
              </button>
              <button
                onClick={toggleExpanded}
                title={isExpanded ? 'Restore chat size' : 'Maximize chat'}
                className={`p-1 rounded transition-colors ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
              >
                {isExpanded ? <RestoreIcon className="w-5 h-5" /> : <MaximizeIcon className="w-5 h-5" />}
              </button>
              <button onClick={onClose} title="Close" className={`p-1 rounded transition-colors ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}>
                <HiOutlineX className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* ── Context pin input (slides in below header) ─────────────────── */}
          {showContextInput && (
            <div className={`px-4 pb-3 pt-2.5 border-b ${isDark ? 'border-white/10 bg-white/[0.025]' : 'border-gray-200/60 bg-white/35'} animate-[fadeIn_0.15s_ease-out]`}>
              <div className={`flex gap-2 ${isExpanded ? 'max-w-5xl mx-auto' : ''}`}>
                <input
                  ref={contextInputRef}
                  type="text"
                  value={contextInputDraft}
                  onChange={(e) => setContextInputDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSetContextPin();
                    if (e.key === 'Escape') setShowContextInput(false);
                  }}
                  placeholder="e.g. Q4 2025  ·  Branch: Nairobi  ·  Customer: ABC Corp"
                  className={`flex-1 px-3 py-1.5 text-xs rounded-lg border ${isDark ? 'bg-white/8 border-white/10 text-slate-50 placeholder:text-slate-500' : 'bg-white/85 border-white/60 text-gray-900 placeholder:text-gray-400'} focus:outline-none focus:ring-1 focus:ring-emerald-400`}
                />
                <button
                  onClick={handleSetContextPin}
                  className="px-3 py-1.5 text-xs font-medium bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors"
                >
                  Set
                </button>
                {contextPin && (
                  <button
                    onClick={handleClearContextPin}
                    className={`px-2 py-1.5 text-xs rounded-lg transition-colors ${isDark ? 'text-slate-300 hover:bg-white/10' : 'text-gray-600 hover:bg-gray-100'}`}
                  >
                    Clear
                  </button>
                )}
              </div>
              <p className={`mt-1.5 text-[10px] ${isExpanded ? 'max-w-5xl mx-auto' : ''} ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                Q AI will apply this scope to all questions in this conversation. Press Esc to cancel.
              </p>
            </div>
          )}

          {/* ── Welcome section (shown before first message) ───────────────── */}
          {!hasUserMessages && !loading && !streaming && (
            isExpanded ? (
              /* ── Expanded welcome — no card wrapper, no overflow clipping ── */
              <div className="max-w-5xl mx-auto px-6 pt-6 pb-2 animate-[fadeIn_0.25s_ease-out]">
                {/* Identity row */}
                <div className="flex items-center gap-3 mb-5">
                  <div className={`w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-white/10 text-emerald-300 border border-white/10' : 'bg-emerald-50 text-emerald-600 border border-emerald-100'}`}>
                    <HiOutlineSparkles className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[15px] font-semibold tracking-tight ${isDark ? 'text-slate-50' : 'text-slate-900'}`}>Q AI</span>
                      <span className={`text-[15px] font-light ${isDark ? 'text-slate-500' : 'text-slate-300'}`}>·</span>
                      <span className={`text-[13px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Business Copilot</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${isDark ? 'border-emerald-400/20 bg-emerald-400/10 text-emerald-300' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
                        Ready
                      </span>
                    </div>
                    <p className={`mt-0.5 text-[11px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                      {pageLabel} · Ask about your live data, workflows, or ask for a chart
                    </p>
                  </div>
                </div>

                {/* Four capability tiles — equal width, no nesting, no overflow */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
                  <div className={`rounded-2xl border p-4 ${isDark ? 'border-white/10 bg-white/[0.025]' : 'border-slate-200/70 bg-white/80'}`}>
                    <div className={`text-[10px] font-bold uppercase tracking-[0.14em] mb-2 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>Summaries</div>
                    <p className={`text-[12px] leading-[1.55] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Revenue, receivables, cash position, and stock — on demand.</p>
                  </div>
                  <div className={`rounded-2xl border p-4 ${isDark ? 'border-white/10 bg-white/[0.025]' : 'border-slate-200/70 bg-white/80'}`}>
                    <div className={`text-[10px] font-bold uppercase tracking-[0.14em] mb-2 ${isDark ? 'text-cyan-400' : 'text-sky-600'}`}>Drill Down</div>
                    <p className={`text-[12px] leading-[1.55] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Compare periods, explain changes, jump straight to the screen.</p>
                  </div>
                  <div className={`rounded-2xl border p-4 ${isDark ? 'border-white/10 bg-white/[0.025]' : 'border-slate-200/70 bg-white/80'}`}>
                    <div className={`text-[10px] font-bold uppercase tracking-[0.14em] mb-2 ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>Action Assist</div>
                    <p className={`text-[12px] leading-[1.55] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Create journal drafts and document stubs directly from the conversation.</p>
                  </div>
                  <div className={`rounded-2xl border p-4 ${isDark ? 'border-white/10 bg-white/[0.025]' : 'border-slate-200/70 bg-white/80'}`}>
                    <div className={`text-[10px] font-bold uppercase tracking-[0.14em] mb-2 ${isDark ? 'text-violet-400' : 'text-violet-600'}`}>Charts</div>
                    <p className={`text-[12px] leading-[1.55] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Ask for a bar chart, pie chart, or table — Q renders it inline.</p>
                  </div>
                </div>
              </div>
            ) : (
              /* ── Collapsed welcome — compact card (unchanged) ── */
              <div className={`mx-4 mt-3 mb-1 rounded-[22px] border overflow-hidden ${isDark ? 'border-white/10 bg-white/[0.035] shadow-[0_12px_30px_rgba(2,6,23,0.22)]' : 'border-white/70 bg-white/85 shadow-[0_12px_30px_rgba(15,23,42,0.08)]'}`}>
                <div className={`h-1.5 ${isDark ? 'bg-gradient-to-r from-cyan-300 via-emerald-400 to-amber-300' : 'bg-gradient-to-r from-sky-400 via-emerald-500 to-amber-400'}`} />
                <div className="p-3.5">
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-white/10 text-emerald-300 border border-white/10' : 'bg-white text-emerald-600 border border-white/70'}`}>
                      <HiOutlineSparkles className="w-5 h-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`font-semibold text-sm ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>Q AI is ready to help.</p>
                      <p className={`text-xs mt-0.5 leading-relaxed ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                        Ask for a summary, a report, a chart, or a drill-down.
                      </p>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        <span className={`text-[10px] px-2 py-1 rounded-full ${isDark ? 'bg-white/8 text-slate-300 border border-white/10' : 'bg-slate-50 text-slate-600 border border-gray-200'}`}>Sales</span>
                        <span className={`text-[10px] px-2 py-1 rounded-full ${isDark ? 'bg-white/8 text-slate-300 border border-white/10' : 'bg-slate-50 text-slate-600 border border-gray-200'}`}>Stock</span>
                        <span className={`text-[10px] px-2 py-1 rounded-full ${isDark ? 'bg-white/8 text-slate-300 border border-white/10' : 'bg-slate-50 text-slate-600 border border-gray-200'}`}>Cash flow</span>
                        <span className={`text-[10px] px-2 py-1 rounded-full ${isDark ? 'bg-white/8 text-slate-300 border border-white/10' : 'bg-slate-50 text-slate-600 border border-gray-200'}`}>Customers</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )
          )}

          {/* ── Messages ───────────────────────────────────────────────────── */}
          <div ref={chatRef} className={messageViewportClass}>
            <div className={conversationRailClass}>
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} ${
                  msg.role === 'assistant' && !msg.done ? '' : 'animate-[fadeIn_0.25s_ease-out]'
                }`}
              >
                <div className={msg.role === 'assistant' ? assistantMessageWidthClass : userMessageWidthClass}>
                  {msg.role === 'assistant' && isExpanded && (
                    <div className="mb-2 ml-12 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-500/90 dark:text-emerald-300/80">
                      <span>Q AI analysis</span>
                      {msg.done && <span className={`h-px flex-1 ${isDark ? 'bg-white/10' : 'bg-slate-200'}`} />}
                    </div>
                  )}
                  <div className={`flex items-end gap-2 ${isExpanded ? 'gap-3' : ''}`}>
                    {msg.role === 'assistant' && (
                      <div className={`rounded-full flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-white/10 text-emerald-300' : 'bg-emerald-100 text-emerald-700'} ${isExpanded ? 'w-9 h-9' : 'w-7 h-7'}`}>
                        <HiOutlineSparkles className="w-3.5 h-3.5" />
                      </div>
                    )}
                    <div className={`whitespace-pre-wrap ${msg.role === 'user' ? userMessageCardClass : assistantMessageCardClass}`}>
                      {msg.role === 'assistant' && !msg.done ? (
                        <TypewriterText text={msg.content} speed={25} onDone={markLastDone} />
                      ) : msg.role === 'assistant' ? (
                        <FormatText
                          text={msg.content}
                          onNavigate={handleDrillDown}
                          onAskAbout={isExpanded ? (q) => sendMessage(q) : undefined}
                          isExpanded={isExpanded}
                          isDark={isDark}
                        />
                      ) : (
                        msg.content
                      )}
                    </div>

                    {/* Assistant actions: copy + feedback — dimmed by default, full on hover */}
                    {msg.role === 'assistant' && msg.done && idx > 0 && (
                      <div className="flex flex-col gap-0.5 mt-1 opacity-30 group-hover:opacity-100 transition-opacity">
                        <CopyButton text={msg.content} isDark={isDark} />
                        <FeedbackButton msgIndex={idx} feedback={msg.feedback} onFeedback={handleFeedback} isDark={isDark} />
                      </div>
                    )}

                    {/* User action: save prompt — always visible, dimmed unless saved or hovered */}
                    {msg.role === 'user' && (
                      <div className="flex items-center mb-1 self-end order-first">
                        <StarButton
                          prompt={msg.content}
                          isSaved={savedPrompts.includes(msg.content)}
                          onSave={handleSavePrompt}
                          onRemove={handleRemoveSavedPrompt}
                          isDark={isDark}
                        />
                      </div>
                    )}
                  </div>

                  {/* Suggested Actions — a message may carry several (e.g. one
                      payment card per supplier due this week). Each renders
                      itself from its server descriptor. */}
                  {msg.role === 'assistant' && msg.done && msg.actions?.length > 0 && (
                    <div className={`mt-2 space-y-2 ${isExpanded ? '' : 'ml-9'}`}>
                      {msg.actions.map((descriptor) => (
                        <SuggestedActionCard
                          key={descriptor.action_id}
                          descriptor={descriptor}
                          isDark={isDark}
                          onCompleted={handleActionCompleted}
                          onNavigate={handleActionNavigate}
                        />
                      ))}
                    </div>
                  )}

                  {/* Chart / table visualizations (charts array; msg.chart kept for persisted messages) */}
                  {msg.role === 'assistant' && msg.done && (msg.charts?.length > 0 || msg.chart) && (
                    <div className={`mt-3 space-y-3 ${isExpanded ? '' : 'ml-9'}`}>
                      {(msg.charts?.length > 0 ? msg.charts : [msg.chart]).map((chart, chartIdx) => (
                        <ChartBlock key={chartIdx} chart={chart} isDark={isDark} />
                      ))}
                    </div>
                  )}

                  {/* Follow-up suggestions — shown only below the last completed assistant message */}
                  {msg.role === 'assistant' && msg.done && idx === messages.length - 1 && !loading && !streaming && msg.follow_ups?.length > 0 && (
                    <div className={`mt-3 flex flex-wrap gap-2 animate-[fadeIn_0.3s_ease-out] ${isExpanded ? 'ml-12' : 'ml-9'}`}>
                      <span className={`text-[10px] font-medium self-center ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Ask next:</span>
                      {msg.follow_ups.slice(0, 3).map((q, qi) => (
                        <button
                          key={qi}
                          onClick={() => sendMessage(q)}
                          disabled={loading || streaming}
                          className={`text-xs px-3 py-1.5 rounded-full border transition-all disabled:opacity-50 ${
                            isDark
                              ? 'border-white/10 bg-white/[0.04] text-slate-200 hover:bg-white/[0.09] hover:border-white/20'
                              : 'border-slate-200 bg-white/88 text-slate-700 hover:bg-white hover:border-emerald-200 hover:text-emerald-700'
                          } shadow-sm backdrop-blur-sm`}
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start animate-[fadeIn_0.2s_ease-out]">
                <div className={`flex items-end ${isExpanded ? 'gap-3' : 'gap-2'}`}>
                  <div className={`rounded-full flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-white/10 text-emerald-300' : 'bg-emerald-100 text-emerald-700'} ${isExpanded ? 'w-9 h-9' : 'w-7 h-7'}`}>
                    <HiOutlineSparkles className="w-3.5 h-3.5" />
                  </div>
                  <div className={`${isExpanded ? 'rounded-[26px]' : 'rounded-lg rounded-bl-none'} px-4 py-3 ${assistantBubbleClass}`}>
                    <div className="flex gap-1.5">
                      <div className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
            </div>
          </div>

          {/* ── Quick questions ────────────────────────────────────────────── */}
          {!loading && !streaming && !hasUserMessages && (
            <div className={`animate-[fadeIn_0.3s_ease-out] ${isExpanded ? 'px-6 pb-3' : 'px-4 pb-1'}`}>

              {/* Saved prompts row */}
              {savedPrompts.length > 0 && (
                <div className={`mb-3 ${isExpanded ? 'max-w-5xl mx-auto' : ''}`}>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <HiBookmark className={`w-3 h-3 ${isDark ? 'text-amber-400' : 'text-amber-500'}`} />
                    <span className={`text-[10px] font-semibold uppercase tracking-[0.12em] ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>Saved</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {savedPrompts.map((p) => (
                      <div key={p} className="flex items-center gap-0.5">
                        <button
                          onClick={() => sendMessage(p)}
                          className={`${quickQuestionClass} whitespace-nowrap max-w-[220px] truncate`}
                          title={p}
                        >
                          {p}
                        </button>
                        <button
                          onClick={() => handleRemoveSavedPrompt(p)}
                          title="Remove"
                          className={`p-0.5 rounded transition-colors ${isDark ? 'text-slate-500 hover:text-rose-400' : 'text-gray-400 hover:text-rose-500'}`}
                        >
                          <HiOutlineX className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Page-specific / default quick questions */}
              {isExpanded ? (
                <div className="mx-auto grid max-w-5xl gap-2.5 sm:grid-cols-2 xl:grid-cols-4">
                  {quickQuestions.map((q) => {
                    const tag = /overdue|unpaid|owe|balance|credit/i.test(q) ? 'Receivables'
                      : /revenue|sales|invoice|customer/i.test(q) ? 'Sales'
                      : /stock|inventory|item|reorder|transfer/i.test(q) ? 'Inventory'
                      : /expense|journal|post|entry|account|bank|cash/i.test(q) ? 'Finance'
                      : /supplier|purchase|grv|payable/i.test(q) ? 'Purchasing'
                      : /production|work order|bom|manufactur/i.test(q) ? 'Manufacturing'
                      : /summarize|summary|overview|today|position/i.test(q) ? 'Overview'
                      : 'Ask Q';
                    return (
                      <button
                        key={q}
                        onClick={() => sendMessage(q)}
                        className={`rounded-[18px] border px-3.5 py-2.5 text-left transition-all ${isDark ? 'border-white/10 bg-white/[0.035] hover:bg-white/[0.07] hover:border-white/20' : 'border-white/70 bg-white/88 hover:bg-white hover:border-emerald-200'} shadow-[0_8px_24px_rgba(15,23,42,0.05)]`}
                      >
                        <div className={`text-[9px] font-semibold uppercase tracking-[0.14em] ${isDark ? 'text-emerald-400/70' : 'text-emerald-600/70'}`}>{tag}</div>
                        <div className={`mt-1.5 text-[13px] font-medium leading-5 ${isDark ? 'text-slate-100' : 'text-slate-800'}`}>{q}</div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-center gap-2 overflow-x-auto no-scrollbar py-1">
                  <p className={`text-[10px] font-medium whitespace-nowrap ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Try asking:</p>
                  {quickQuestions.map((q) => (
                    <button
                      key={q}
                      onClick={() => sendMessage(q)}
                      className={`${quickQuestionClass} whitespace-nowrap py-1`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Saved prompts strip (always visible when prompts exist) ──── */}
          {savedPrompts.length > 0 && (
            <div className={`border-t ${isDark ? 'border-white/10' : 'border-gray-100'} px-4 py-2 ${isExpanded ? 'px-6' : ''}`}>
              <div className={`flex items-center gap-2 ${isExpanded ? 'max-w-4xl mx-auto' : ''}`}>
                <div className={`flex items-center gap-1 flex-shrink-0 ${isDark ? 'text-amber-400' : 'text-amber-500'}`}>
                  <HiBookmark className="w-3 h-3" />
                  <span className={`text-[10px] font-semibold uppercase tracking-[0.12em] ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>Saved</span>
                </div>
                <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar flex-1 min-w-0">
                  {savedPrompts.map((p) => (
                    <div key={p} className="flex items-center gap-0.5 flex-shrink-0">
                      <button
                        onClick={() => sendMessage(p)}
                        disabled={loading || streaming}
                        title={p}
                        className={`text-[11px] px-2.5 py-1 rounded-full border whitespace-nowrap max-w-[180px] truncate transition-all disabled:opacity-50 ${
                          isDark
                            ? 'border-white/10 bg-white/[0.04] text-slate-200 hover:bg-white/[0.09] hover:border-white/20'
                            : 'border-gray-200 bg-white/80 text-slate-600 hover:bg-white hover:border-amber-200 hover:text-amber-700'
                        }`}
                      >
                        {p}
                      </button>
                      <button
                        onClick={() => handleRemoveSavedPrompt(p)}
                        title="Remove"
                        className={`p-0.5 rounded flex-shrink-0 transition-colors ${isDark ? 'text-slate-600 hover:text-rose-400' : 'text-gray-300 hover:text-rose-400'}`}
                      >
                        <HiOutlineX className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── Input ──────────────────────────────────────────────────────── */}
          <div className={`border-t ${isDark ? 'border-white/10' : 'border-gray-200'} ${isExpanded ? 'sticky bottom-0 z-20 px-6 py-5 backdrop-blur-xl' : 'p-4'}`}>
            <div className={`${isExpanded ? 'mx-auto max-w-4xl' : ''}`}>
              <div className={`rounded-2xl p-2 border ${isDark ? 'border-white/10 bg-white/[0.04] shadow-[0_12px_28px_rgba(2,6,23,0.2)]' : 'border-white/70 bg-white/85 shadow-[0_12px_28px_rgba(15,23,42,0.08)]'} backdrop-blur-xl`}>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                    placeholder="Ask about your business..."
                    className={inputClass}
                    disabled={loading || streaming}
                  />
                  <button
                    onClick={() => sendMessage()}
                    disabled={!input.trim() || loading || streaming}
                    className="p-2.5 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-[0_8px_20px_rgba(16,185,129,0.25)]"
                  >
                    <HiOutlinePaperAirplane className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className={`mt-2 flex items-center justify-between gap-2 ${isExpanded ? 'px-1' : ''}`}>
                <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Q AI may make mistakes</p>
                {isExpanded && (
                  <p className={`text-[10px] ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>Esc to restore</p>
                )}
              </div>
            </div>
          </div>

          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes aiPanelIn {
          from { opacity: 0; transform: translateX(10px); }
          to { opacity: 1; transform: translateX(0); }
        }

        @keyframes aiPanelBorderSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes aiPanelDashMove {
          from { stroke-dashoffset: 0; }
          to { stroke-dashoffset: -190; }
        }

        @keyframes aiPanelGlowBreath {
          0%, 100% { opacity: 0.52; }
          50% { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

const AIChat = ({ onClose, currentPage = '/', isSidebarCollapsed = false }) => {
  const { isAiCopilotEnabled, isAiCopilotDeniedForUser, user } = useAuth();

  // Two different closed doors, two different messages: telling someone whose
  // own account was opted out to go and enable AI in Settings would send them
  // after a switch that is already on.
  if (!isAiCopilotEnabled) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <HiOutlineSparkles className="w-10 h-10 text-gray-300 dark:text-slate-600 mb-3" />
        {isAiCopilotDeniedForUser ? (
          <>
            <p className="text-sm font-medium text-gray-500 dark:text-slate-400">Q AI Copilot is not enabled for your account</p>
            <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">Contact your administrator if you need access.</p>
          </>
        ) : (
          <>
            <p className="text-sm font-medium text-gray-500 dark:text-slate-400">AI features are currently disabled</p>
            <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">Contact your administrator to enable AI in Settings → General → Artificial Intelligence.</p>
          </>
        )}
      </div>
    );
  }

  const scope = storageScope(user?.tenantDb, user?.tenant_registry_id, user?.id);

  // `key` matters: the message/pin/prompt state is seeded from localStorage in
  // useState initializers, which only run on mount. Without remounting on a
  // scope change, a tenant switch that leaves this component alive would keep
  // the previous tenant's transcript in memory and write it under the new key.
  return (
    <AIChatInner
      key={scope}
      onClose={onClose}
      currentPage={currentPage}
      isSidebarCollapsed={isSidebarCollapsed}
      scope={scope}
    />
  );
};

export default AIChat;
